import React, { useCallback, useMemo } from 'react';
import BundleContainer from './BundleContainer';
import PanelList from './PanelList';
import { getActiveBundles, getActivePanels } from './productDefinitions';

const Alcat = ({ orderableItem, onSelection, selectedComponents }) => {
	// Create lists of bundles and panels that are active for the orderable item
	const bundles = useMemo(() => getActiveBundles({ orderableItem }), [orderableItem]);
	const foodPanels = useMemo(() => getActivePanels({ orderableItem, type: 'food' }), [orderableItem]);
	const advancedPanels = useMemo(() => getActivePanels({ orderableItem, type: 'advanced' }), [orderableItem]);

	/**
	 * Add or Remove items from the selectedComponents array based on what was clicked, and then
	 * submit the new array to onSelection.
	 *
	 * Bundles and Food Panels are mutually exclusive (only 1 can be selected at a time).
	 * When a bundle or food panel is selected, remove any other selected bundles or food panels (including
	 * any children of selected bundles).
	 */
	const handleAlcatPanelSelect = useCallback(
		(inputFieldId) => {
			const isPreviouslySelected = selectedComponents?.findIndex((id) => id === inputFieldId) > -1;
			const isBundle = (id) => bundles?.some((bundle) => bundle.id === id);
			const isFoodPanel = (id) => foodPanels?.some((panel) => panel.id === id);

			// Only 1 of a bundle or food panel can be selected at a time.
			const isMutuallyExclusive = (id) => isBundle(id) || isFoodPanel(id);

			if (isMutuallyExclusive(inputFieldId)) {
				const bundle = bundles.find((bundle) => bundle.id === inputFieldId);
				const bundleComponentIds = bundle?.panels?.map((panel) => panel.id) || [];

				if (isPreviouslySelected) {
					// Bundle already selected -> remove bundle and children
					onSelection(
						selectedComponents.filter((id) => id !== inputFieldId && !bundleComponentIds.includes(id))
					);
					return;
				} else {
					// Bundle not already selected -> add bundle. Remove children of new bundle and other bundles/food panels.
					onSelection([
						...selectedComponents
							.filter((id) => !isMutuallyExclusive(id))
							.filter((id) => !bundleComponentIds.includes(id)),
						inputFieldId
					]);
					return;
				}
			}

			// Already selected -> remove it
			if (isPreviouslySelected) {
				onSelection(selectedComponents.filter((id) => id !== inputFieldId));
				return;
			}

			// Not already selected -> add it
			onSelection([...selectedComponents, inputFieldId]);
		},
		[bundles, foodPanels, onSelection, selectedComponents]
	);

	const findDetailByInputFieldId = (inputFieldId) => {
		return (
			bundles?.find((bundle) => bundle.id === inputFieldId) ||
			foodPanels?.find((panel) => panel.id === inputFieldId) ||
			advancedPanels?.find((panel) => panel.id === inputFieldId)
		);
	};

	// Convert selected inputFieldIds into their corresponding bundle/panel details.
	const selectionDetails = selectedComponents?.map((id) => findDetailByInputFieldId(id)).filter(Boolean) || [];

	if (!bundles.length && !foodPanels.length && !advancedPanels.length) {
		return null;
	}

	// Don't pass an onSelect when the original onSelection is undefined. This triggers the input fields to be disabled.
	const selectionHandler = !onSelection ? null : handleAlcatPanelSelect;

	return (
		<div style={{ fontSize: '.875em' }}>
			<h3 className="d-flex gap-3 align-items-end mb-4">
				<img src="/images/cellscience/alcattest.webp" alt="Alcat Test" style={{ height: '1.1em' }} />
				<small className="text-reset">FOOD / CHEMICAL SENSITIVITY PANELS</small>
			</h3>

			<BundleContainer bundles={bundles} onSelect={selectionHandler} selections={selectionDetails} />

			{(foodPanels.length > 0 || advancedPanels.length > 0) && (
				<>
					{bundles?.length > 0 && (
						<div
							className="d-flex align-items-center hr-separator gap-3 my-4"
							style={{ '--hr-line-color': 'rgba(0, 0, 0, .25)' }}
						>
							<span className="text-muted">or</span>
						</div>
					)}

					<div className="mb-5">
						<h4 className="p-0 text-black">Customize / Build Your Own Panel</h4>

						<div className="card">
							<div className="card-body">
								<div className="row row-cols-1 row-cols-md-2">
									{foodPanels?.length > 0 && (
										<div>
											<h5 className="bold fs-6 m-0 text-black px-2 py-3 pt-1">Food Panels</h5>
											<PanelList
												panels={foodPanels}
												selectionType="radio"
												onSelect={selectionHandler}
												selections={selectionDetails}
											/>
										</div>
									)}

									{advancedPanels?.length > 0 && (
										<div>
											<h5 className="bold fs-6 m-0 text-black px-2 py-3 pt-1">Advanced Panels</h5>
											<PanelList
												panels={advancedPanels}
												selectionType="checkbox"
												onSelect={selectionHandler}
												selections={selectionDetails}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default Alcat;
